"use client";

import { useFormContext } from "react-hook-form";
import { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { calculateMonthlySummary } from "@/lib/life-simulation";
import { useDebouncedWatch } from "../hooks/use-debounced-watch";
import { useI18n } from "@/lib/i18n/hook";
import { useCurrency as useCurrencyContext } from "../contexts/currency-context";

export function SummaryPanel() {
  const { t } = useI18n();
  const { currency } = useCurrencyContext();
  const [summary, setSummary] = useState({
    monthlyIncome: 0,
    monthlyExpense: 0,
    monthlySavings: 0,
    totalAssets: 0,
    totalLiabilities: 0,
    netWorth: 0,
    monthlyInvestment: 0,
  });

  const updateSummary = useCallback((formData: any) => {
    if (formData) {
      const calculatedSummary = calculateMonthlySummary(formData);
      setSummary(calculatedSummary);
    }
  }, []);

  useDebouncedWatch(updateSummary);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">
          {t("lifePlan.result.summary.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="space-y-2">
          <div className="flex justify-between py-1">
            <dt className="text-sm font-medium text-muted-foreground">
              {t("lifePlan.result.summary.monthlyIncome")}
            </dt>
            <dd className="text-sm font-medium">
              {summary.monthlyIncome.toLocaleString()}
              {currency}
            </dd>
          </div>
          <div className="flex justify-between py-1">
            <dt className="text-sm font-medium text-muted-foreground">
              {t("lifePlan.result.summary.monthlyExpense")}
            </dt>
            <dd className="text-sm font-medium">
              {summary.monthlyExpense.toLocaleString()}
              {currency}
            </dd>
          </div>
          <div className="flex justify-between py-1 border-t">
            <dt className="text-sm font-medium text-muted-foreground">
              {t("lifePlan.result.summary.monthlyInvestment")}
            </dt>
            <dd className="text-sm font-medium">
              {summary.monthlyInvestment.toLocaleString()}
              {currency}
            </dd>
          </div>
          <div className="flex justify-between py-1">
            <dt className="text-sm font-medium">
              {t("lifePlan.result.summary.monthlySavings")}
            </dt>
            <dd
              className={`text-sm font-medium ${
                summary.monthlySavings < 0 ? "text-red-500" : "text-green-500"
              }`}
            >
              {summary.monthlySavings.toLocaleString()}
              {currency}
            </dd>
          </div>
          <div className="flex justify-between py-1 mt-4 border-t">
            <dt className="text-sm font-medium text-muted-foreground">
              {t("lifePlan.result.summary.totalAssets")}
            </dt>
            <dd className="text-sm font-medium">
              {summary.totalAssets.toLocaleString()}
              {currency}
            </dd>
          </div>
          <div className="flex justify-between py-1">
            <dt className="text-sm font-medium text-muted-foreground">
              {t("lifePlan.result.summary.totalLiabilities")}
            </dt>
            <dd className="text-sm font-medium">
              {summary.totalLiabilities.toLocaleString()}
              {currency}
            </dd>
          </div>
          <div className="flex justify-between py-1 border-t">
            <dt className="text-sm font-medium">
              {t("lifePlan.result.summary.netWorth")}
            </dt>
            <dd
              className={`text-sm font-medium ${
                summary.netWorth < 0 ? "text-red-500" : ""
              }`}
            >
              {summary.netWorth.toLocaleString()}
              {currency}
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
